import axios from 'axios';
import { DEFAULT_COUNSELLOR_MODE } from '../counsellorModes';

// Prefer an explicit API base URL from env in dev; fall back to same-origin /api.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
// Auth token getter for non-axios requests (e.g. fetch-based SSE streaming).
// Registered by AuthWrapper via setAuthTokenGetter().
let _authTokenGetter = null;

axios.defaults.withCredentials = true;

export const setAuthTokenGetter = (getter) => {
  _authTokenGetter = getter;
};

export const getGuestAccessStatus = async () => {
  const response = await axios.get(`${API_BASE_URL}/guest/access`);
  return response.data;
};

export const startGuestAccess = async () => {
  const response = await axios.post(`${API_BASE_URL}/guest/access`);
  return response.data;
};

export const endGuestAccess = async () => {
  const response = await axios.delete(`${API_BASE_URL}/guest/access`);
  return response.data;
};

// Fired whenever the frontend knows credits were just spent, so listeners
// (e.g. the header credits badge) can refetch without waiting for their poll.
export const CREDITS_CHANGED_EVENT = 'credits-changed';

export const notifyCreditsChanged = () => {
  window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT));
};

export const getUserPreferences = async () => {
  const response = await axios.get(`${API_BASE_URL}/user/preferences`);
  return response.data;
};

export const updateUserPreferences = async (preferences) => {
  const response = await axios.put(`${API_BASE_URL}/user/preferences`, preferences);
  return response.data;
};

export const updateUserAvatarPreference = async ({ avatarId, avatarBackground }) => {
  const response = await axios.put(`${API_BASE_URL}/user/preferences`, {
    avatar_id: avatarId,
    avatar_background: avatarBackground,
  });
  return response.data;
};

export const getUserAccess = async () => {
  const response = await axios.get(`${API_BASE_URL}/user/access`);
  return response.data;
};

// Get available profiles
export const getProfiles = async () => {
  const response = await axios.get(`${API_BASE_URL}/profiles`);
  return response.data;
};

// Start new session (with optional custom profile)
export const startSession = async (profileIdx, clientType = 'Profile-Reveal', useApi = false, customProfile = null) => {
  const body = {
    profile_idx: profileIdx,
    client_type: clientType,
    use_api: useApi,
  };
  if (customProfile !== null) {
    body.custom_profile = customProfile;
  }
  const response = await axios.post(`${API_BASE_URL}/session/start`, body);
  return response.data;
};

// Start a client-mode session (human plays client, AI plays counsellor)
export const startClientModeSession = async (counsellorMode = DEFAULT_COUNSELLOR_MODE) => {
  const response = await axios.post(`${API_BASE_URL}/session/start`, {
    role: 'client',
    counsellor_mode: counsellorMode,
  });
  return response.data;
};

// If no bytes arrive from an SSE stream for this long, treat the connection as
// stalled (e.g. upstream hung) and surface an error instead of hanging forever.
const SSE_IDLE_TIMEOUT_MS = 120_000;

// Shared SSE reader for all streaming endpoints. Dispatches (eventName, data)
// to onEvent; onEvent may return true to stop reading. Two robustness guards:
//   - a malformed `data:` line is skipped, never killing the whole stream;
//   - if the server connects but goes silent past SSE_IDLE_TIMEOUT_MS, the
//     stream is aborted and onStall() is invoked so the UI can recover.
async function consumeSseStream(response, controller, onEvent, { onStall } = {}) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  let idleTimer = null;
  const armIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (onStall) onStall();
      controller.abort();
    }, SSE_IDLE_TIMEOUT_MS);
  };
  armIdle();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      armIdle(); // received bytes — reset the stall timer

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete line for the next chunk

      let currentEvent = null;
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ') && currentEvent) {
          let data;
          try {
            data = JSON.parse(line.slice(6));
          } catch (_) {
            currentEvent = null;
            continue; // skip a malformed line rather than killing the stream
          }
          const stop = onEvent(currentEvent, data);
          currentEvent = null;
          if (stop) return;
        } else if (line === '' || line.startsWith(': ')) {
          currentEvent = null; // blank separator or comment/heartbeat
        }
      }
    }
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
  }
}

// Send human client message and stream AI counsellor response (SSE)
export const sendClientMessageStream = (message, onToken, onMetadata, onError) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = { 'Content-Type': 'application/json' };
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
      } catch (_) {}
    }

    const response = await fetch(`${API_BASE_URL}/session/client-message/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }

    await consumeSseStream(response, controller, (event, data) => {
      switch (event) {
        case 'token':
          onToken(data.token);
          break;
        case 'metadata':
          onMetadata(data);
          break;
        case 'error':
          onError(data.error || 'Unknown stream error');
          return true; // stop reading on error
        default:
          break;
      }
      return false;
    }, { onStall: () => onError('The response timed out. Please try again.') });
  };

  const promise = run().catch((err) => {
    if (err.name !== 'AbortError') {
      onError(err.message || 'Stream failed');
    }
  });

  return { promise, abort: () => controller.abort() };
};

// Export a custom client profile as a .xlsx file (returns a Blob)
export const exportProfile = async (profile) => {
  const response = await axios.post(`${API_BASE_URL}/profile/export`, { profile }, { responseType: 'blob' });
  return response.data;
};

// Import a custom client profile from a .xlsx file (returns profile JSON)
export const importProfile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_BASE_URL}/profile/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.profile;
};

// Send counselor message with streaming response (SSE)
export const sendMessageStream = (message, onToken, onMetadata, onError, callbacks = {}) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = { 'Content-Type': 'application/json' };

    // Include Cognito auth token if available (mirrors axios interceptor)
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }
      } catch (_) {
        // Auth not available — continue without token
      }
    }

    const response = await fetch(`${API_BASE_URL}/session/message/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }

    await consumeSseStream(response, controller, (event, data) => {
      switch (event) {
        case 'token':
          onToken(data.token);
          break;
        case 'classification':
          callbacks.onClassification?.(data);
          break;
        case 'guidance':
          callbacks.onGuidance?.(data.feedback || '');
          break;
        case 'guidance_error':
          callbacks.onGuidanceError?.(data.error || 'Guidance could not be generated.');
          break;
        case 'metadata':
          onMetadata(data);
          break;
        case 'error':
          onError(data.error);
          break;
        case 'done':
          break;
        default:
          break;
      }
      return false;
    }, { onStall: () => onError('The response timed out. Please try again.') });
  };

  const promise = run().catch((err) => {
    if (err.name !== 'AbortError') {
      onError(err.message || 'Stream failed');
    }
  });

  return { promise, abort: () => controller.abort() };
};

// Full Session voice turn (SSE). Same events as sendMessageStream plus
// `client_response`, which carries the finished reply before guidance completes
// so it can be spoken immediately. Non-2xx responses reject with `status` set.
export const streamRealtimeTurn = (transcript, callbacks = {}) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = { 'Content-Type': 'application/json' };
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
      } catch (_) {}
    }

    const response = await fetch(`${API_BASE_URL}/realtime/respond`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ transcript }),
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const error = new Error(errBody.error || `HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }

    let stalled = false;
    try {
      await consumeSseStream(response, controller, (event, data) => {
        switch (event) {
          case 'classification':
            callbacks.onClassification?.(data);
            break;
          case 'client_response':
            callbacks.onClientResponse?.(data.text || '');
            break;
          case 'guidance':
            callbacks.onGuidance?.(data.feedback || '');
            break;
          case 'guidance_error':
            callbacks.onGuidanceError?.(data.error || 'Guidance could not be generated.');
            break;
          case 'metadata':
            callbacks.onMetadata?.(data);
            break;
          case 'error':
            throw new Error(data.error || 'Unknown stream error');
          default:
            break;
        }
        return false;
      }, { onStall: () => { stalled = true; } });
    } catch (err) {
      if (!stalled) throw err;
    }
    if (stalled) throw new Error('The response timed out. Please try again.');
  };

  return { promise: run(), abort: () => controller.abort() };
};

// End session
export const endSession = async () => {
  const response = await axios.post(`${API_BASE_URL}/session/end`);
  notifyCreditsChanged();
  return response.data;
};

// Get session status
export const getSessionStatus = async () => {
  const response = await axios.get(`${API_BASE_URL}/session/status`);
  return response.data;
};

// Run LLM vs LLM mode
export const runLlmMode = async (profileIdx, clientType = 'Profile-Reveal', useApi = false, maxTurns = 120, counsellorMode = DEFAULT_COUNSELLOR_MODE) => {
  const response = await axios.post(`${API_BASE_URL}/session/llm-mode`, {
    profile_idx: profileIdx,
    client_type: clientType,
    use_api: useApi,
    max_turns: maxTurns,
    counsellor_mode: counsellorMode
  });
  return response.data;
};

// Connect to LLM mode SSE stream (replaces polling)
// Returns { abort } to disconnect the stream.
// Callbacks: onCounsellorMessage(data), onClientToken(data), onTurnMeta(data),
//            onConversationEnd(data), onError(errorMsg)
export const connectLlmModeStream = (onCounsellorMessage, onClientToken, onTurnMeta, onConversationEnd, onError) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = {};
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
      } catch (_) {}
    }

    const response = await fetch(`${API_BASE_URL}/session/llm-mode/stream`, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }

    await consumeSseStream(response, controller, (event, data) => {
      switch (event) {
        case 'counsellor_message': onCounsellorMessage(data); break;
        case 'client_token':       onClientToken(data); break;
        case 'turn_meta':          onTurnMeta(data); break;
        case 'conversation_end':   onConversationEnd(data); break;
        case 'error':
          onError(data.error);
          break;
        default: break;
      }
      return false;
    }, { onStall: () => onError('The response timed out. Please try again.') });
  };

  run().catch((err) => {
    if (err.name !== 'AbortError') {
      onError(err.message || 'LLM stream failed');
    }
  });

  return { abort: () => controller.abort() };
};

// Get LLM mode analysis-pipeline status (for ReportPanel polling — no conversation messages)
export const getLlmModeStatus = async () => {
  const response = await axios.get(`${API_BASE_URL}/session/llm-mode/status`);
  return response.data;
};

// Cancel LLM mode
export const cancelLlmMode = async () => {
  const response = await axios.post(`${API_BASE_URL}/session/llm-mode/cancel`);
  return response.data;
};

// Pause/resume LLM mode
export const pauseLlmMode = async () => {
  const response = await axios.post(`${API_BASE_URL}/session/llm-mode/pause`);
  return response.data;
};

// Cancel analysis (AutoMISC, PDF generation)
export const cancelAnalysis = async () => {
  const response = await axios.post(`${API_BASE_URL}/session/analysis/cancel`);
  return response.data;
};

// Reset session state (on refresh)
export const resetSession = async () => {
  const response = await axios.post(`${API_BASE_URL}/session/reset`);
  return response.data;
};

// Download PDF report
export const downloadPdf = async () => {
  const response = await axios.get(`${API_BASE_URL}/session/download-pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

// Download annotated transcript CSV
export const downloadCsv = async () => {
  const response = await axios.get(`${API_BASE_URL}/session/download-csv`, {
    responseType: 'blob',
  });
  return response.data;
};

// OARS: list all exercise metadata
export const getOarsExercises = async () => {
  const response = await axios.get(`${API_BASE_URL}/oars/exercises`);
  return response.data;
};

// OARS: fetch full exercise JSON (conversation_history + target_turn)
export const getOarsExercise = async (exerciseId) => {
  const response = await axios.get(`${API_BASE_URL}/oars/exercise/${exerciseId}`);
  return response.data;
};

// OARS: generate a new exercise for supported skill types
export const generateOarsExercise = async (skillType, options = {}) => {
  const response = await axios.post(`${API_BASE_URL}/oars/generate-exercise`, {
    skill_type: skillType,
    ...options,
  });
  notifyCreditsChanged();
  return response.data;
};

export const startOarsAttempt = async ({
  exerciseId,
  skillType,
  generatedExerciseId = null,
  selectedSkillType,
  retryOfAttemptId = null,
}) => {
  const response = await axios.post(`${API_BASE_URL}/oars/start-attempt`, {
    exercise_id: exerciseId,
    skill_type: skillType,
    generated_exercise_id: generatedExerciseId,
    selected_skill_type: selectedSkillType,
    retry_of_attempt_id: retryOfAttemptId,
  });
  return response.data;
};

export const startScalingRulersExercise = async () => {
  const response = await axios.post(`${API_BASE_URL}/specific-strategies/scaling-rulers/start`, {});
  notifyCreditsChanged();
  return response.data;
};

export const retryScalingRulersExercise = async (attemptId) => {
  const response = await axios.post(
    `${API_BASE_URL}/specific-strategies/scaling-rulers/${attemptId}/retry`,
    {},
  );
  return response.data;
};

export const respondToScalingRulersExercise = async (attemptId, message) => {
  const response = await axios.post(
    `${API_BASE_URL}/specific-strategies/scaling-rulers/${attemptId}/respond`,
    { message }
  );
  notifyCreditsChanged();
  return response.data;
};

export const respondToScalingRulersExerciseStream = (attemptId, message, callbacks = {}) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) headers.Authorization = `Bearer ${idToken}`;
      } catch (_) {}
    }

    const response = await fetch(
      `${API_BASE_URL}/specific-strategies/scaling-rulers/${attemptId}/respond`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ message }),
        credentials: 'include',
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${response.status}`);
    }

    let completedPayload = null;
    let streamError = null;
    await consumeSseStream(response, controller, (event, data) => {
      if (event === 'classification') {
        callbacks.onClassification?.(data);
      } else if (event === 'client_reply') {
        callbacks.onClientReply?.(data.reply || '');
      } else if (event === 'complete') {
        completedPayload = data;
        callbacks.onComplete?.(data);
        notifyCreditsChanged();
        return true;
      } else if (event === 'error') {
        streamError = new Error(data.error || 'Client response stream failed');
        return true;
      }
      return false;
    });

    if (streamError) throw streamError;
    if (!completedPayload) throw new Error('Client response stream ended before completion');
    return completedPayload;
  };

  return { controller, abort: () => controller.abort(), promise: run() };
};

export const streamScalingRulersEvaluation = (attemptId, callbacks = {}) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = { Accept: 'text/event-stream' };
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) headers.Authorization = `Bearer ${idToken}`;
      } catch (_) {}
    }

    const response = await fetch(
      `${API_BASE_URL}/specific-strategies/scaling-rulers/${attemptId}/evaluate`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${response.status}`);
    }

    const responseContentType = response.headers?.get?.('content-type') || '';
    if (!responseContentType.includes('text/event-stream')) {
      const completedPayload = await response.json();
      callbacks.onComplete?.(completedPayload);
      notifyCreditsChanged();
      return completedPayload;
    }

    let completedPayload = null;
    let streamError = null;
    await consumeSseStream(response, controller, (event, data) => {
      if (event === 'guidance') {
        callbacks.onGuidance?.(data.feedback || '');
      } else if (event === 'complete') {
        completedPayload = data;
        callbacks.onComplete?.(data);
        notifyCreditsChanged();
        return true;
      } else if (event === 'error') {
        streamError = new Error(data.error || 'Evaluation stream failed');
        return true;
      }
      return false;
    });

    if (streamError) throw streamError;
    if (!completedPayload) throw new Error('Evaluation stream ended before completion');
    return completedPayload;
  };

  return { controller, abort: () => controller.abort(), promise: run() };
};

// OARS: annotate a trainee utterance against an exercise
export const annotateOarsUtterance = async (
  exerciseId,
  utterance,
  generatedExerciseId = null,
  exercisePayload = null,
  exerciseSignature = null,
  options = {}
) => {
  const response = await axios.post(`${API_BASE_URL}/oars/annotate`, {
    exercise_id: exerciseId,
    generated_exercise_id: generatedExerciseId,
    exercise_payload: exercisePayload,
    exercise_signature: exerciseSignature,
    utterance,
    specific_skill_attempt_id: options.specificSkillAttemptId || null,
    selected_skill_type: options.selectedSkillType || null,
    retry_of_attempt_id: options.retryOfAttemptId || null,
  });
  notifyCreditsChanged();
  return response.data;
};

// OARS: stream classifier metadata and evaluator guidance, then resolve with
// the same completed payload returned by annotateOarsUtterance.
export const annotateOarsUtteranceStream = (
  exerciseId,
  utterance,
  generatedExerciseId = null,
  exercisePayload = null,
  exerciseSignature = null,
  options = {},
  callbacks = {}
) => {
  const controller = new AbortController();

  const run = async () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (_authTokenGetter) {
      try {
        const idToken = await _authTokenGetter();
        if (idToken) headers.Authorization = `Bearer ${idToken}`;
      } catch (_) {}
    }

    const response = await fetch(`${API_BASE_URL}/oars/annotate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        exercise_id: exerciseId,
        generated_exercise_id: generatedExerciseId,
        exercise_payload: exercisePayload,
        exercise_signature: exerciseSignature,
        utterance,
        specific_skill_attempt_id: options.specificSkillAttemptId || null,
        selected_skill_type: options.selectedSkillType || null,
        retry_of_attempt_id: options.retryOfAttemptId || null,
      }),
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }

    const responseContentType = response.headers?.get?.('content-type') || '';
    if (!responseContentType.includes('text/event-stream')) {
      const completedPayload = await response.json();
      callbacks.onClassification?.({
        predicted_labels: completedPayload.predicted_labels || [],
        classifier_explanation: completedPayload.classifier_explanation || '',
        matched_labels: completedPayload.matched_labels || [],
        full_match: Boolean(completedPayload.full_match),
        partial_match: Boolean(completedPayload.partial_match),
      });
      callbacks.onGuidance?.(completedPayload.evaluation?.feedback || '');
      callbacks.onComplete?.(completedPayload);
      notifyCreditsChanged();
      return completedPayload;
    }

    let completedPayload = null;
    let streamError = null;
    await consumeSseStream(response, controller, (event, data) => {
      switch (event) {
        case 'classification':
          callbacks.onClassification?.(data);
          break;
        case 'guidance':
          callbacks.onGuidance?.(data.feedback || '');
          break;
        case 'complete':
          completedPayload = data;
          callbacks.onComplete?.(data);
          notifyCreditsChanged();
          return true;
        case 'error':
          streamError = new Error(data.error || 'Evaluation stream failed');
          return true;
        default:
          break;
      }
      return false;
    }, {
      onStall: () => {
        streamError = new Error('The evaluation timed out. Please try again.');
      },
    });

    if (streamError) throw streamError;
    if (!completedPayload) throw new Error('Evaluation stream ended before completion');
    return completedPayload;
  };

  return { promise: run(), abort: () => controller.abort() };
};

// Submit contact message
export const submitContactMessage = async (subject, body) => {
  const response = await axios.post(`${API_BASE_URL}/contact/message`, {
    subject,
    body
  });
  return response.data;
};

export const submitFreeformFeedback = async ({ q1Score, q2Score, q3Score, feedback }) => {
  const response = await axios.post(`${API_BASE_URL}/feedback/freeform`, {
    q1_score: q1Score,
    q2_score: q2Score,
    q3_score: q3Score,
    feedback,
  });
  return response.data;
};

export const submitSpecificSkillsFeedback = async ({ q1Score, q2Score, q3Score, feedback }) => {
  const response = await axios.post(`${API_BASE_URL}/feedback/specific-skills`, {
    q1_score: q1Score,
    q2_score: q2Score,
    q3_score: q3Score,
    feedback,
  });
  return response.data;
};

export const submitEvaluatorReport = async ({ attemptId, exerciseId, sessionInput, evaluatorFeedback, reportText }) => {
  const response = await axios.post(`${API_BASE_URL}/feedback/evaluator-report`, {
    attempt_id: attemptId,
    exercise_id: exerciseId,
    session_input: sessionInput,
    evaluator_feedback: evaluatorFeedback,
    report_text: reportText,
  });
  return response.data;
};

export const submitStrategyEvaluatorReport = async ({ attemptId, feedbackType, reportText }) => {
  const response = await axios.post(`${API_BASE_URL}/feedback/strategy-evaluator-report`, {
    attempt_id: attemptId,
    feedback_type: feedbackType,
    report_text: reportText,
  });
  return response.data;
};

export const submitFullSessionEvaluatorReport = async ({ sessionId, turnIndex, reportText }) => {
  const response = await axios.post(`${API_BASE_URL}/feedback/full-session-evaluator-report`, {
    session_id: sessionId || null,
    turn_index: turnIndex,
    report_text: reportText,
  });
  return response.data;
};

export const getAccountSpecificSkills = async () => {
  const response = await axios.get(`${API_BASE_URL}/account/specific-skills`);
  return response.data;
};

export const getAccountSpecificSkillReplay = async (attemptId) => {
  const response = await axios.get(`${API_BASE_URL}/account/specific-skills/${attemptId}/replay`);
  return response.data;
};

export const getAccountSpecificStrategies = async () => {
  const response = await axios.get(`${API_BASE_URL}/account/specific-strategies`);
  return response.data;
};

export const getAccountSpecificStrategyReplay = async (attemptId) => {
  const response = await axios.get(`${API_BASE_URL}/account/specific-strategies/${attemptId}/replay`);
  return response.data;
};

export const getAccountFullSessions = async () => {
  const response = await axios.get(`${API_BASE_URL}/account/full-sessions`);
  return response.data;
};

export const getAccountCredits = async () => {
  const response = await axios.get(`${API_BASE_URL}/account/credits`);
  return response.data;
};

export const getAccountTokenUsage = async (range) => {
  const response = await axios.get(`${API_BASE_URL}/account/token-usage`, {
    params: {
      range,
      tz_offset: new Date().getTimezoneOffset(),
    },
  });
  return response.data;
};

export const getAccountFullSessionReplay = async (sessionId) => {
  const response = await axios.get(`${API_BASE_URL}/account/full-sessions/${sessionId}/replay`);
  return response.data;
};

export const downloadAccountFullSessionReport = async (sessionId) => {
  const response = await axios.get(`${API_BASE_URL}/account/full-sessions/${sessionId}/report`, {
    responseType: 'blob',
  });
  return response.data;
};

export const downloadAccountFullSessionTranscript = async (sessionId) => {
  const response = await axios.get(`${API_BASE_URL}/account/full-sessions/${sessionId}/transcript`, {
    responseType: 'blob',
  });
  return response.data;
};
